import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import type { Devanning, DevanningItem } from '../types'
import { fmtDate, fmtDateTime } from '../types'
import { BlankDevanSheetPDF } from './BlankDevanSheet'
import { generateDevanSheetExcelBlob, devanSheetExcelFilename } from './DevanSheetExcel'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from '../useColumnPrefs'
import { SortableColHeader, ColumnToggleButton } from '../ColDragHeader'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { useToast } from '../../../hooks/useToast'

const WF_COL_DEFS = [
  { id: 'destination', label: 'Destination' },
  // Z50.3: 'split' shows the user-defined group name ("Group 1", "LBA4") — was
  // mislabeled "Split No" (that's a different concept, see 'splitno' below).
  { id: 'split',       label: 'Group No' },
  { id: 'splitno',     label: 'Split No' },
  { id: 'nm',          label: 'N/M' },
  { id: 'jobref',      label: 'Job Ref' },
  { id: 'mode',        label: 'Del. Mode' },
  { id: 'qty',         label: 'Qty' },
  { id: 'wt',          label: 'WT(KG)' },
  { id: 'cbm',         label: 'CBM' },
  { id: 'hold',        label: 'Hold' },
  { id: 'skipdetail',  label: 'Skip' },
  { id: 'pallets',     label: 'P1–P15' },
  { id: 'tplts',       label: 'T.PLTS' },
  { id: 'actsum',      label: 'Act Sum' },
  // Z50.5: warehouse location entered after devanning (e.g. "BHX ETSF Area", "KW3044").
  { id: 'pltlocation', label: 'PLT Location' },
  { id: 'check',       label: '✓' },
  { id: 'note',        label: 'Note' },
] as const

const COL_WIDTHS: Record<string, number> = {
  destination: 90, split: 90, splitno: 70, nm: 170, jobref: 140, mode: 70,
  qty: 50, wt: 60, cbm: 55, hold: 45, skipdetail: 45,
  pallets: 330, tplts: 60, actsum: 60, pltlocation: 110, check: 35, note: 200,
}

const DEV_STATUS_COLORS: Record<string, string> = {
  awaiting:  'bg-indigo-100 text-indigo-700',
  started:   'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
}

const PALLET_COUNT = 15

interface DestGroup { groupName: string; items: DevanningItem[] }

interface PostCompletionEdit {
  id: string
  devanning_id: string
  devanning_item_id: string
  field_changed: string
  from_value: string | null
  to_value: string | null
  reason: string
  changed_at: string
  changed_by: string
}

const EDIT_FIELD_LABELS: Record<string, string> = {
  manual_pallet_total:     'T.PLTS',
  manual_carton_sum:       'Act Sum',
  pallets_detail:          'P1–P15 pallet detail',
  actual_cartons_received: 'Act Ctns',
}

function splitSortKey(name: string): number {
  const m = name.match(/(?:devan split|group)\s+(\d+)/i)
  return m ? parseInt(m[1]) : Infinity
}

// #283 renamed "Devan Split N" -> "Group N"; legacy plans predating that rename
// (e.g. EISU9276073) still carry the old name in the DB, so normalize on display
// too — belt-and-suspenders alongside the one-time data migration.
function cleanGroupName(name: string): string {
  return name.replace(/\s*[—–-].*$/, '').trim().replace(/^Devan Split\s+/i, 'Group ')
}

function groupByGroupName(items: DevanningItem[]): DestGroup[] {
  const seen = new Map<string, DevanningItem[]>()
  for (const it of items) {
    const key = it.group_name || it.delivery_address || '(No Address)'
    if (!seen.has(key)) seen.set(key, [])
    seen.get(key)!.push(it)
  }
  return Array.from(seen.entries())
    .map(([groupName, its]) => ({ groupName, items: its }))
    .sort((a, b) => splitSortKey(a.groupName) - splitSortKey(b.groupName))
}

function initPallets(first: DevanningItem): number[] {
  const arr = Array(PALLET_COUNT).fill(0)
  for (const pd of (first.pallets_detail || [])) {
    const idx = (((pd as any).p ?? (pd as any).pallet_no) as number) - 1
    if (idx >= 0 && idx < PALLET_COUNT && pd.ctns > 0) arr[idx] = pd.ctns
  }
  return arr
}

// ─── Post-completion inventory sync ──────────────────────────────────────────
// When a completed devanning's grid cells are edited, mirror the change into
// warehouse_inventory and write an inventory_adjustments audit row.
async function syncInventoryPostCompletion(
  firstItemId: string,
  changes: { cartons?: number; pallets_qty?: number },
  changedBy: string,
): Promise<void> {
  const { data: inv } = await supabase
    .from('warehouse_inventory')
    .select('id, cartons, pallets_qty')
    .eq('devanning_item_id', firstItemId)
    .maybeSingle()
  if (!inv) return
  const patch: Record<string, number> = {}
  const inserts: Record<string, unknown>[] = []
  for (const [field, newVal] of Object.entries(changes) as ['cartons' | 'pallets_qty', number][]) {
    if (newVal === undefined) continue
    const oldVal: number = (inv as any)[field] ?? 0
    patch[field] = newVal
    if (oldVal !== newVal) {
      inserts.push({
        inventory_id:  inv.id,
        field_changed: field,
        from_value:    oldVal,
        to_value:      newVal,
        delta:         newVal - oldVal,
        reason:        'Post-completion correction',
        changed_by:    changedBy,
      })
    }
  }
  if (Object.keys(patch).length) {
    await supabase.from('warehouse_inventory').update(patch).eq('id', inv.id)
  }
  if (inserts.length) {
    await supabase.from('inventory_adjustments').insert(inserts)
  }
}

// Z50.X.1: PDF/Excel generation is synchronous, CPU-heavy work (react-pdf layout,
// ExcelJS write) with no network call involved — a hung promise here means the
// generator itself never resolved, not a stalled request. This is a best-effort
// safety net: it can't preempt a truly blocking synchronous loop (JS is
// single-threaded, so the setTimeout below only fires once the call stack
// yields), but it does catch a promise that legitimately never settles.
class ExportTimeoutError extends Error {}
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new ExportTimeoutError(`Timed out after ${Math.round(ms / 1000)}s`)), ms)
    promise.then(
      v => { clearTimeout(timer); resolve(v) },
      e => { clearTimeout(timer); reject(e) },
    )
  })
}

const EXPORT_TIMEOUT_MS = 60_000
const EXPORT_SLOW_MS    = 3_000

function ExportSpinner() {
  return <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden="true" />
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DevanningWorkflow() {
  const { id, did } = useParams<{ id: string; did: string }>()
  const navigate = useNavigate()
  const { userEmail, user } = useAuth()

  const [devanning,      setDevanning]      = useState<Devanning | null>(null)
  const [items,          setItems]          = useState<DevanningItem[]>([])
  const [warehouseCode,  setWarehouseCode]  = useState('')
  const [completing,     setCompleting]     = useState(false)
  // Z50.X.1: one shared "export in flight" gate across all 4 buttons (PDF/Excel ×
  // Full/Simple) — previously PDF and Excel tracked separate loading flags, so
  // nothing stopped a user clicking a PDF button then, before its disabled state
  // painted, also clicking an Excel button — stacking two heavy synchronous
  // generations and freezing the tab. Only one export can run at a time now.
  const [exportJob, setExportJob] = useState<{ kind: 'pdf' | 'xlsx'; type: 'full' | 'simple' } | null>(null)
  // Guard on a ref (updates synchronously, unlike state which lands next render) so
  // two clicks arriving back-to-back in the same tick can't both slip past the
  // `if (exportJobRef.current) return` check before either sees the other's write.
  const exportJobRef = useRef<{ kind: 'pdf' | 'xlsx'; type: 'full' | 'simple' } | null>(null)
  const [exportSlow, setExportSlow] = useState(false)
  const { toast } = useToast()

  // Mismatch dialog state
  const [completeDialog, setCompleteDialog]   = useState(false)
  const [mismatchList, setMismatchList]       = useState<{ groupName: string; expected: number; actual: number }[]>([])
  const [completeReason, setCompleteReason]   = useState('')

  // Inbound confirmation dialog state
  const [inboundDialog,       setInboundDialog]       = useState(false)
  const [pendingInventoryRows, setPendingInventoryRows] = useState<Record<string, unknown>[]>([])
  const [inboundPushing,      setInboundPushing]      = useState(false)

  // 3-way cancel dialog state
  const [cancelChoice, setCancelChoice] = useState(false)
  const [cancelSubDialog, setCancelSubDialog] = useState<'reschedule' | 'revert' | 'cancel_job' | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [cancelSaving, setCancelSaving] = useState(false)

  // Auto-save indicator
  const [saveTime, setSaveTime] = useState<string | null>(null)

  // Table column sort
  const [wfSortCol, setWfSortCol] = useState<string | null>(null)
  const [wfSortDir, setWfSortDir] = useState<'asc' | 'desc'>('asc')
  const toggleSort = (col: string) => {
    setWfSortCol(prev => { if (prev === col) { setWfSortDir(d => d === 'asc' ? 'desc' : 'asc'); return col } setWfSortDir('asc'); return col })
  }

  // Column drag-reorder + show/hide + widths
  const { visibleCols: wfVisibleCols, hidden: wfHidden, order: wfOrder, setOrder: setWfOrder,
    toggleHidden: toggleWfCol, reset: resetWfCols, loaded: wfLoaded,
    extra: wfExtra, setExtra: setWfExtra } =
    useColumnPrefs('col_pref_wf', WF_COL_DEFS, user?.id, { dbKey: 'col_pref_wf_front' }, { widths: {} })

  // Column resize — widths stored in wfExtra.widths; auto-fit computed below after groups
  const savedColWidths = (wfExtra.widths as Record<string, number>) ?? {}

  const saveColWidth = (colId: string, w: number) =>
    setWfExtra({ ...wfExtra, widths: { ...savedColWidths, [colId]: w } })

  const resetColWidth = (colId: string) => {
    const n = { ...savedColWidths }; delete n[colId]
    setWfExtra({ ...wfExtra, widths: n })
  }

  const wfSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const handleWfColDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (over && active.id !== over.id) {
      const oldIdx = wfOrder.indexOf(active.id as string)
      const newIdx = wfOrder.indexOf(over.id as string)
      setWfOrder(arrayMove(wfOrder, oldIdx, newIdx))
    }
  }

  // Header editable fields
  const [haulierInput,    setHaulierInput]    = useState('')
  const [arrivalAtInput,  setArrivalAtInput]  = useState('')
  const [savingHeader,    setSavingHeader]    = useState(false)

  // WYSIWYG grid state
  const [pallets,          setPallets]          = useState<Record<string, number[]>>({})
  const [holds,            setHolds]            = useState<Record<string, boolean>>({})
  const [actualCtns,       setActualCtns]       = useState<Record<string, number>>({})
  const [rowNotes,         setRowNotes]         = useState<Record<string, string>>({})
  const [rowPltLocation,   setRowPltLocation]   = useState<Record<string, string>>({})
  const [skipDetail,       setSkipDetail]       = useState<Record<string, boolean>>({})
  const [manualPalletTotal,setManualPalletTotal]= useState<Record<string, number>>({})
  const [manualCartonSum,  setManualCartonSum]  = useState<Record<string, number>>({})
  const [confirmSkipGroup, setConfirmSkipGroup] = useState<string | null>(null)
  const [saveStatus,  setSaveStatus]  = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const devanningRef       = useRef<Devanning | null>(null)
  const userEmailRef       = useRef<string | null | undefined>(undefined)
  const sessionReasonRef   = useRef<string | null>(null)
  const reasonResolversRef = useRef<Array<(r: string | null) => void>>([])
  const reasonDialogOpenRef = useRef(false)
  const didRef             = useRef(did)

  const [sessionReason,    setSessionReason]    = useState<string | null>(null)
  const [showReasonDialog, setShowReasonDialog] = useState(false)
  const [reasonInput,      setReasonInput]      = useState('')
  const [pendingEditInfo,  setPendingEditInfo]  = useState<{ field: string; from: string; to: string } | null>(null)
  const [editLog,          setEditLog]          = useState<PostCompletionEdit[]>([])
  const [showEditLog,      setShowEditLog]      = useState(false)

  // ── Data loading ─────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!did) return
    const [{ data: dev }, { data: its }, { data: wh }] = await Promise.all([
      supabase.from('devannings')
        .select('*, plan:devanning_plans(container_no, vessel, eta_date, carrier, planned_devan_at, source_loading_list:loading_lists(job_ref))')
        .eq('id', did).single(),
      supabase.from('devanning_items').select('*, customer:customers(code, name), plan_item:devanning_plan_items(client_ref)').eq('devanning_id', did).order('group_name').order('id'),
      id ? supabase.from('warehouses').select('code').eq('id', id).maybeSingle() : Promise.resolve({ data: null }),
    ])
    const d = dev as Devanning
    setDevanning(d)
    setWarehouseCode((wh as any)?.code || '')
    setHaulierInput(d.haulier || '')
    setArrivalAtInput(d.arrival_at ? new Date(d.arrival_at).toISOString().slice(0, 16) : '')

    const loaded = (its as DevanningItem[]) || []
    setItems(loaded)

    const initP:   Record<string, number[]> = {}
    const initH:   Record<string, boolean>  = {}
    const initA:   Record<string, number>   = {}
    const initN:   Record<string, string>   = {}
    const initLoc: Record<string, string>   = {}
    const initSkip: Record<string, boolean> = {}
    const initMPT:  Record<string, number>  = {}
    const initMCS:  Record<string, number>  = {}
    for (const g of groupByGroupName(loaded)) {
      const first = g.items[0]
      if (!first) continue
      initP[g.groupName]    = initPallets(first)
      initH[g.groupName]    = (first as any).hold_flag ?? false
      const sk = (first as any).skip_pallet_detail ?? true
      initA[g.groupName]    = sk
        ? ((first as any).manual_carton_sum ?? 0)
        : ((first as any).actual_cartons_received ?? 0)
      initN[g.groupName]    = (first as any).notes ?? ''
      initLoc[g.groupName]  = (first as any).plt_location ?? ''
      initSkip[g.groupName] = sk
      initMPT[g.groupName]  = (first as any).manual_pallet_total ?? 0
      initMCS[g.groupName]  = (first as any).manual_carton_sum ?? 0
    }
    setPallets(initP)
    setHolds(initH)
    setActualCtns(initA)
    setRowNotes(initN)
    setRowPltLocation(initLoc)
    setSkipDetail(initSkip)
    setManualPalletTotal(initMPT)
    setManualCartonSum(initMCS)
    const { data: log } = await supabase
      .from('devanning_post_completion_edits')
      .select('*')
      .eq('devanning_id', did)
      .order('changed_at', { ascending: false })
    setEditLog((log as PostCompletionEdit[]) || [])
  }, [did])

  useEffect(() => { fetchData() }, [fetchData])
  useEffect(() => { devanningRef.current = devanning },  [devanning])
  useEffect(() => { userEmailRef.current = userEmail },  [userEmail])
  useEffect(() => { didRef.current = did },              [did])

  // ── Sync devanning_items from plan_items (recovery when devanning was created before items existed) ──
  const [syncingFromPlan, setSyncingFromPlan] = useState(false)
  const [syncPlanError, setSyncPlanError] = useState<string | null>(null)

  const handleSyncFromPlan = useCallback(async () => {
    if (!devanning?.plan_id || !did) return
    setSyncingFromPlan(true); setSyncPlanError(null)
    try {
      const [{ data: planItems, error: piErr }, { data: existingItems }] = await Promise.all([
        supabase.from('devanning_plan_items').select('*').eq('plan_id', devanning.plan_id).order('sort_order'),
        supabase.from('devanning_items').select('id, plan_item_id').eq('devanning_id', did),
      ])
      if (piErr) throw piErr
      if (!planItems || planItems.length === 0) { setSyncPlanError('Plan has no items yet — sync from LL first.'); return }

      // Merge: add new items, update plan fields on existing ones (preserve manual pallet data)
      const existingByPlanItemId = new Map((existingItems || []).map((i: any) => [i.plan_item_id, i.id]))
      const planItemIds = new Set(planItems.map((pi: any) => pi.id))

      // Delete orphan devanning_items (plan_item was removed)
      const orphanIds = (existingItems || []).filter((i: any) => !planItemIds.has(i.plan_item_id)).map((i: any) => i.id)
      if (orphanIds.length > 0) await supabase.from('devanning_items').delete().in('id', orphanIds)

      // Insert new items / update plan fields on existing ones
      const toInsert: any[] = []
      for (const pi of planItems) {
        const existingId = existingByPlanItemId.get(pi.id)
        if (existingId) {
          // Update structural plan fields only — leave manual_pallet_total, manual_carton_sum, pallets_detail, notes intact
          await supabase.from('devanning_items').update({
            hbl_no: pi.hbl_no, description: pi.description ?? null,
            delivery_address: pi.delivery_address, delivery_method: pi.delivery_method ?? null,
            delivery_method_id: pi.delivery_method_id ?? null,
            expected_cartons: pi.expected_cartons, expected_weight_kg: pi.expected_weight_kg,
            expected_cbm: pi.expected_cbm, group_name: pi.group_name ?? null,
            split_no: pi.split_no ?? null, system_ref: pi.system_ref ?? null,
          }).eq('id', existingId)
        } else {
          toInsert.push({
            devanning_id: did, plan_item_id: pi.id,
            hbl_no: pi.hbl_no, description: pi.description ?? null,
            customer_id: pi.customer_id ?? null,
            delivery_address: pi.delivery_address, delivery_method: pi.delivery_method ?? null,
            delivery_method_id: pi.delivery_method_id ?? null,
            expected_cartons: pi.expected_cartons, expected_weight_kg: pi.expected_weight_kg,
            expected_cbm: pi.expected_cbm, actual_cartons: 0, pallet_count: 0, pallets_qty: 0,
            pallets_detail: [], condition: 'good',
            split_no: pi.split_no ?? null, system_ref: pi.system_ref ?? null,
            group_name: pi.group_name ?? null,
          })
        }
      }
      if (toInsert.length > 0) {
        const { error: insErr } = await supabase.from('devanning_items').insert(toInsert)
        if (insErr) throw insErr
      }
      await fetchData()
    } catch (e: any) {
      setSyncPlanError(e.message || 'Sync failed')
    } finally {
      setSyncingFromPlan(false)
    }
  }, [devanning?.plan_id, did, fetchData])

  // ── Post-completion reason dialog ─────────────────────────────────────────

  const requireReasonAsync = useCallback((info?: { field: string; from: string; to: string }): Promise<string | null> => {
    if (sessionReasonRef.current !== null) return Promise.resolve(sessionReasonRef.current)
    return new Promise(resolve => {
      reasonResolversRef.current.push(resolve)
      if (!reasonDialogOpenRef.current) {
        reasonDialogOpenRef.current = true
        if (info) setPendingEditInfo(info)
        setShowReasonDialog(true)
        setReasonInput('')
      }
    })
  }, [])

  const confirmReason = () => {
    const r = reasonInput.trim()
    sessionReasonRef.current = r
    setSessionReason(r)
    setShowReasonDialog(false)
    reasonDialogOpenRef.current = false
    const resolvers = [...reasonResolversRef.current]
    reasonResolversRef.current = []
    resolvers.forEach(fn => fn(r))
    setReasonInput('')
    setPendingEditInfo(null)
  }

  const dismissReasonDialog = () => {
    setShowReasonDialog(false)
    reasonDialogOpenRef.current = false
    const resolvers = [...reasonResolversRef.current]
    reasonResolversRef.current = []
    resolvers.forEach(fn => fn(null))
    setPendingEditInfo(null)
  }

  const insertEditLog = useCallback(async (
    itemId: string,
    field: string,
    fromVal: string | null,
    toVal: string | null,
    reason: string,
  ) => {
    const { data: row } = await supabase.from('devanning_post_completion_edits').insert({
      devanning_id:      didRef.current,
      devanning_item_id: itemId,
      field_changed:     field,
      from_value:        fromVal,
      to_value:          toVal,
      reason,
      changed_by:        userEmailRef.current || 'unknown',
    }).select().single()
    if (row) setEditLog(prev => [row as PostCompletionEdit, ...prev])
  }, [])

  // ── Header save ───────────────────────────────────────────────────────────────

  const saveHeaderFields = async () => {
    setSavingHeader(true)
    await supabase.from('devannings').update({
      haulier:    haulierInput || null,
      arrival_at: arrivalAtInput ? new Date(arrivalAtInput).toISOString() : null,
    }).eq('id', did!)
    setDevanning(prev => prev
      ? { ...prev, haulier: haulierInput || null, arrival_at: arrivalAtInput ? new Date(arrivalAtInput).toISOString() : null }
      : prev)
    setSavingHeader(false)
  }

  // ── Status actions ────────────────────────────────────────────────────────────

  const handleStartDevanning = async () => {
    if (!confirm('Start devanning now?')) return
    const now = new Date().toISOString()
    await supabase.from('devannings').update({ status: 'started', devanning_started_at: now, devanned_by: userEmail || 'unknown' }).eq('id', did!)
    setDevanning(prev => prev ? { ...prev, status: 'started', devanning_started_at: now, devanned_by: userEmail || 'unknown' } : prev)
  }

  const handleCancel = () => {
    setCancelChoice(true)
    setCancelReason('')
    setRescheduleDate(
      (devanning as any)?.plan?.planned_devan_at
        ? new Date((devanning as any).plan.planned_devan_at).toISOString().slice(0, 16)
        : ''
    )
  }

  const handleCancelAction = async () => {
    if (!cancelSubDialog || cancelReason.trim().length < 5) return
    setCancelSaving(true)
    const now = new Date().toISOString()
    const actor = userEmail || 'unknown'
    const patch = { last_reason: cancelReason, last_changed_at: now, last_changed_by: actor }
    try {
      if (cancelSubDialog === 'reschedule') {
        const iso = rescheduleDate ? new Date(rescheduleDate).toISOString() : null
        if (devanning?.plan_id) {
          await supabase.from('devanning_plans').update({ planned_devan_at: iso }).eq('id', devanning.plan_id)
        }
        await supabase.from('devannings').update(patch).eq('id', did!)
        setDevanning(prev => prev ? { ...prev, ...patch } : prev)
        setCancelChoice(false); setCancelSubDialog(null)
      } else if (cancelSubDialog === 'revert') {
        if (devanning?.status === 'started') {
          // Revert to Awaiting — keep devanning, just reset status
          const { error } = await supabase.from('devannings').update({
            status: 'awaiting', devanning_started_at: null, ...patch,
          }).eq('id', did!)
          if (error) throw error
          setDevanning(prev => prev ? { ...prev, status: 'awaiting', devanning_started_at: null, ...patch } : prev)
          setCancelChoice(false); setCancelSubDialog(null)
        } else {
          // Revert to Pending — delete devanning, reset plan
          if (devanning?.plan_id) {
            await supabase.from('devanning_plans').update({
              status: 'pending', planned_devan_at: null,
              last_reason: cancelReason, last_changed_at: now, last_changed_by: actor,
            }).eq('id', devanning.plan_id)
          }
          await supabase.from('devanning_items').delete().eq('devanning_id', did!)
          await supabase.from('devannings').delete().eq('id', did!)
          navigate(`/warehouse/${id}/plans${devanning?.plan_id ? `/${devanning.plan_id}` : ''}`)
        }
      } else { // cancel_job
        await supabase.from('devannings').update({ status: 'cancelled', ...patch }).eq('id', did!)
        if (devanning?.plan_id) {
          await supabase.from('devanning_plans').update({
            status: 'cancelled', last_reason: cancelReason, last_changed_at: now, last_changed_by: actor,
          }).eq('id', devanning.plan_id)
        }
        setDevanning(prev => prev ? { ...prev, status: 'cancelled', ...patch } : prev)
        setCancelChoice(false); setCancelSubDialog(null)
      }
    } catch (err: any) {
      alert(err.message)
    } finally {
      setCancelSaving(false)
    }
  }

  // ── Grid save (debounced) ─────────────────────────────────────────────────────

  const saveGroup = useCallback(async (_groupName: string, groupItems: DevanningItem[], pVals: number[], holdVal: boolean) => {
    const first = groupItems[0]
    if (!first) return

    let reason = ''
    if (devanningRef.current?.status === 'completed') {
      const totalCartons = pVals.reduce((s, v) => s + v, 0)
      const totalPallets = pVals.filter(v => v > 0).length
      const r = await requireReasonAsync({
        field: EDIT_FIELD_LABELS['pallets_detail'],
        from: '(previous)',
        to: `${totalCartons} ctns / ${totalPallets} pallets`,
      })
      if (!r) return
      reason = r
    }

    const detail = pVals.map((ctns, i) => ({ p: i + 1, ctns })).filter(x => x.ctns > 0)
    setSaveStatus('saving')
    const { error } = await supabase.from('devanning_items').update({
      pallets_detail:           detail,
      hold_flag:                holdVal,
      actual_cartons_received:  pVals.reduce((s, v) => s + v, 0) || null,
    }).eq('id', first.id)
    setSaveStatus(error ? 'error' : 'saved')
    if (!error) {
      const t = new Date()
      setSaveTime(`${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}:${String(t.getSeconds()).padStart(2, '0')}`)
      if (reason) {
        const totalCartons = pVals.reduce((s, v) => s + v, 0)
        const totalPallets = pVals.filter(v => v > 0).length
        await insertEditLog(first.id, 'pallets_detail', null, `${totalCartons} ctns / ${totalPallets} plts`, reason)
        await syncInventoryPostCompletion(first.id, { cartons: totalCartons, pallets_qty: totalPallets }, userEmailRef.current || 'unknown')
      }
    }
  }, [requireReasonAsync, insertEditLog])

  const debounceSave = (groupName: string, groupItems: DevanningItem[], pVals: number[], holdVal: boolean) => {
    clearTimeout(debounceRef.current[groupName])
    debounceRef.current[groupName] = setTimeout(() => saveGroup(groupName, groupItems, pVals, holdVal), 500)
  }

  const updatePallet = (groupName: string, groupItems: DevanningItem[], pIdx: number, val: number) => {
    const newP = [...(pallets[groupName] || Array(PALLET_COUNT).fill(0))]
    newP[pIdx] = val
    setPallets(prev => ({ ...prev, [groupName]: newP }))
    // Z50.3: actual_cartons_received now only ever comes from Act Sum (the removed
    // Act Ctns column was its only other writer) — auto-sync it to the pallet-grid
    // total here, mirroring what updateManualCartonSum already does in skip mode.
    setActualCtns(prev => ({ ...prev, [groupName]: newP.reduce((s, v) => s + v, 0) }))
    debounceSave(groupName, groupItems, newP, holds[groupName] ?? false)
  }

  const updateHold = (groupName: string, groupItems: DevanningItem[], val: boolean) => {
    setHolds(prev => ({ ...prev, [groupName]: val }))
    debounceSave(groupName, groupItems, pallets[groupName] || Array(PALLET_COUNT).fill(0), val)
  }

  const updateNote = (groupName: string, groupItems: DevanningItem[], val: string) => {
    setRowNotes(prev => ({ ...prev, [groupName]: val }))
    const first = groupItems[0]
    if (!first) return
    clearTimeout(debounceRef.current[`note_${groupName}`])
    debounceRef.current[`note_${groupName}`] = setTimeout(async () => {
      await supabase.from('devanning_items').update({ notes: val || null }).eq('id', first.id)
    }, 500)
  }

  // Z50.5: warehouse location entered after devanning -- free-form text, same pattern as
  // updateNote above (no reason-gate, since this isn't audited data like the pallet/carton
  // totals below).
  const updatePltLocation = (groupName: string, groupItems: DevanningItem[], val: string) => {
    setRowPltLocation(prev => ({ ...prev, [groupName]: val }))
    const first = groupItems[0]
    if (!first) return
    clearTimeout(debounceRef.current[`pltloc_${groupName}`])
    debounceRef.current[`pltloc_${groupName}`] = setTimeout(async () => {
      await supabase.from('devanning_items').update({ plt_location: val || null }).eq('id', first.id)
    }, 500)
  }

  const applySkipDetail = (groupName: string, groupItems: DevanningItem[], val: boolean, clearPallets = false) => {
    setSkipDetail(prev => ({ ...prev, [groupName]: val }))
    if (clearPallets) setPallets(prev => ({ ...prev, [groupName]: Array(PALLET_COUNT).fill(0) }))
    const first = groupItems[0]
    if (!first) return
    const patch: Record<string, unknown> = { skip_pallet_detail: val }
    if (clearPallets) patch.pallets_detail = []
    clearTimeout(debounceRef.current[`skip_${groupName}`])
    debounceRef.current[`skip_${groupName}`] = setTimeout(async () => {
      setSaveStatus('saving')
      const { error } = await supabase.from('devanning_items').update(patch).eq('id', first.id)
      setSaveStatus(error ? 'error' : 'saved')
    }, 300)
  }

  const updateSkipDetail = (groupName: string, groupItems: DevanningItem[], val: boolean) => {
    if (val) {
      const pVals = pallets[groupName] || Array(PALLET_COUNT).fill(0)
      if (pVals.some(v => v > 0)) {
        setConfirmSkipGroup(groupName)
        return
      }
    }
    applySkipDetail(groupName, groupItems, val)
  }

  const updateManualTotal = (groupName: string, groupItems: DevanningItem[], val: number) => {
    const oldVal = manualPalletTotal[groupName] ?? 0
    setManualPalletTotal(prev => ({ ...prev, [groupName]: val }))
    const first = groupItems[0]
    if (!first) return
    clearTimeout(debounceRef.current[`mpt_${groupName}`])
    debounceRef.current[`mpt_${groupName}`] = setTimeout(async () => {
      if (devanning?.status === 'completed') {
        const reason = await requireReasonAsync({
          field: EDIT_FIELD_LABELS['manual_pallet_total'],
          from: String(oldVal),
          to: String(val),
        })
        if (!reason) {
          setManualPalletTotal(prev => ({ ...prev, [groupName]: oldVal }))
          return
        }
        setSaveStatus('saving')
        const { error } = await supabase.from('devanning_items').update({ manual_pallet_total: val || null }).eq('id', first.id)
        if (!error) {
          await insertEditLog(first.id, 'manual_pallet_total', String(oldVal), String(val), reason)
          await syncInventoryPostCompletion(first.id, { pallets_qty: val || 0 }, userEmail || 'unknown')
        }
        setSaveStatus(error ? 'error' : 'saved')
      } else {
        setSaveStatus('saving')
        const { error } = await supabase.from('devanning_items').update({ manual_pallet_total: val || null }).eq('id', first.id)
        setSaveStatus(error ? 'error' : 'saved')
      }
    }, 500)
  }

  const updateManualCartonSum = (groupName: string, groupItems: DevanningItem[], val: number) => {
    const oldVal = manualCartonSum[groupName] ?? 0
    setManualCartonSum(prev => ({ ...prev, [groupName]: val }))
    setActualCtns(prev => ({ ...prev, [groupName]: val }))
    const first = groupItems[0]
    if (!first) return
    clearTimeout(debounceRef.current[`mcs_${groupName}`])
    debounceRef.current[`mcs_${groupName}`] = setTimeout(async () => {
      if (devanning?.status === 'completed') {
        const reason = await requireReasonAsync({
          field: EDIT_FIELD_LABELS['manual_carton_sum'],
          from: String(oldVal),
          to: String(val),
        })
        if (!reason) {
          setManualCartonSum(prev => ({ ...prev, [groupName]: oldVal }))
          setActualCtns(prev => ({ ...prev, [groupName]: oldVal }))
          return
        }
        setSaveStatus('saving')
        const { error } = await supabase.from('devanning_items').update({
          manual_carton_sum:       val || null,
          actual_cartons_received: val || null,
        }).eq('id', first.id)
        if (!error) {
          await insertEditLog(first.id, 'manual_carton_sum', String(oldVal), String(val), reason)
          await syncInventoryPostCompletion(first.id, { cartons: val || 0 }, userEmail || 'unknown')
        }
        setSaveStatus(error ? 'error' : 'saved')
      } else {
        setSaveStatus('saving')
        const { error } = await supabase.from('devanning_items').update({
          manual_carton_sum:       val || null,
          actual_cartons_received: val || null,
        }).eq('id', first.id)
        setSaveStatus(error ? 'error' : 'saved')
      }
    }, 500)
  }

  // ── Mark completed + inventory ────────────────────────────────────────────────

  const handleComplete = () => {
    if (!devanning) return
    const allGroups = groupByGroupName(items)
    const mismatches = allGroups.map(g => {
      const expected = g.items.reduce((s, it) => s + it.expected_cartons, 0)
      const isSkip   = skipDetail[g.groupName] ?? true
      const actual   = isSkip
        ? (manualCartonSum[g.groupName] ?? 0)
        : (pallets[g.groupName] || Array(PALLET_COUNT).fill(0)).reduce((s: number, v: number) => s + v, 0)
      return { groupName: g.groupName, expected, actual }
    }).filter(m => m.expected !== m.actual)

    if (mismatches.length > 0) {
      setMismatchList(mismatches)
      setCompleteReason('')
      setCompleteDialog(true)
      return
    }
    void doComplete('')
  }

  const doComplete = async (reason: string) => {
    if (!devanning) return
    setCompleting(true)
    setCompleteDialog(false)
    const completedAt = new Date().toISOString()
    const { error: devErr } = await supabase.from('devannings').update({
      status: 'completed',
      devanning_completed_at: completedAt,
      devanned_by: devanning.devanned_by || userEmail || 'unknown',
      ...(reason ? { last_reason: reason, last_changed_at: completedAt, last_changed_by: userEmail || 'unknown' } : {}),
    }).eq('id', did!)
    if (devErr) { setCompleting(false); return }

    const allGroups = groupByGroupName(items)
    const planJobRef = (devanning.plan as any)?.source_loading_list?.job_ref || null

    const inventoryRows = allGroups.map(g => {
      const first    = g.items[0]
      if (!first) return null
      const isSkip   = skipDetail[g.groupName] ?? true
      const pVals    = pallets[g.groupName] || Array(PALLET_COUNT).fill(0)
      const totalCtns = isSkip
        ? (manualCartonSum[g.groupName] ?? 0)
        : pVals.reduce((s: number, v: number) => s + v, 0)
      const palletQty = isSkip
        ? (manualPalletTotal[g.groupName] || null)
        : pVals.filter((v: number) => v > 0).length
      const totalCbm  = g.items.reduce((s, it) => s + (it.expected_cbm || 0), 0)
      const totalKgs  = g.items.reduce((s, it) => s + (it.expected_weight_kg || 0), 0)
      const hbls         = g.items.map(it => it.hbl_no).filter(Boolean).join(', ')
      const sysRefs      = [...new Set(g.items.map(it => it.system_ref).filter(Boolean))].join(', ')
      const houseJobRefs = [...new Set(g.items.map(it => (it.plan_item as any)?.client_ref).filter(Boolean))].sort().join(', ')
      return {
        warehouse_id:       devanning.warehouse_id,
        devanning_id:       devanning.id,
        devanning_item_id:  first.id,
        job_ref:            planJobRef,
        split_no:           first.split_no ?? null,
        house_job_refs:     houseJobRefs || null,
        group_name:         g.groupName,
        hbl_no:             hbls || null,
        system_ref:         sysRefs || null,
        description:        g.items.map(it => it.description).filter(Boolean).join('; ') || null,
        delivery_address:   first.delivery_address,
        delivery_method:    first.delivery_method,
        cartons:            totalCtns,
        pallets_qty:        palletQty,
        cbm:                totalCbm || null,
        kgs:                totalKgs || null,
        status:             'in_inbound',
        inbound_at:         completedAt,
      }
    }).filter(Boolean)

    setCompleting(false)
    setPendingInventoryRows(inventoryRows as Record<string, unknown>[])
    setInboundDialog(true)
  }

  // ── Move to Inbound ───────────────────────────────────────────────────────────

  const doMoveToInbound = async () => {
    setInboundPushing(true)
    if (pendingInventoryRows.length > 0) {
      // Dedup: skip rows whose devanning_item_id already exists in warehouse_inventory
      const itemIds = pendingInventoryRows.map((r: any) => r.devanning_item_id).filter(Boolean)
      const existingIds = new Set<string>()
      if (itemIds.length > 0) {
        const { data: existing } = await supabase
          .from('warehouse_inventory').select('devanning_item_id').in('devanning_item_id', itemIds)
        ;(existing || []).forEach((e: any) => { if (e.devanning_item_id) existingIds.add(e.devanning_item_id) })
      }
      const toInsert = pendingInventoryRows.filter((r: any) => !existingIds.has(r.devanning_item_id))
      if (toInsert.length > 0) {
        await supabase.from('warehouse_inventory').insert(toInsert)
      }
    }
    const now = new Date().toISOString()
    await supabase.from('devannings').update({
      pushed_to_inbound: true,
      pushed_to_inbound_at: now,
      pushed_to_inbound_by: userEmail || 'unknown',
    }).eq('id', did!)
    navigate(`/warehouse/${id}/inbound`)
  }

  // ── Blank sheet row data — shared by the PDF and Excel exports so the two ────
  // never drift apart (same source, guaranteed row-for-row parity).
  const buildBlankSheetRows = () => groups.map(g => ({
    // #183: some groups (e.g. DPD) carry no delivery_address at all — fall
    // back to the delivery method so Destination is never blank.
    destination: g.items[0]?.delivery_address || g.items[0]?.delivery_method || '',
    splitNo:     cleanGroupName(g.groupName),
    splitNos:    [...new Set(g.items.map(it => it.split_no).filter(Boolean))].sort().join(', '),
    sysRefs:     [...new Set(g.items.map(it => it.system_ref).filter(Boolean))].sort().join('\n'),
    jobRefs:     [...new Set(g.items.map(it => (it.plan_item as any)?.client_ref).filter(Boolean))].sort().join('\n'),
    delMode:     g.items[0]?.delivery_method || '',
    qty:         g.items.reduce((s, it) => s + it.expected_cartons, 0),
    wtKg:        g.items.reduce((s, it) => s + (it.expected_weight_kg || 0), 0) || null,
    cbm:         g.items.reduce((s, it) => s + (it.expected_cbm || 0), 0) || null,
    isHold:      holds[g.groupName] ?? false,
    skipDetail:  skipDetail[g.groupName] ?? true,
  }))

  // ── PDF blank sheet download ──────────────────────────────────────────────────

  const downloadBlankSheetPDF = async (type: 'full' | 'simple' = 'full') => {
    if (!devanning || exportJobRef.current) return
    exportJobRef.current = { kind: 'pdf', type }
    setExportJob(exportJobRef.current)
    const slowTimer = setTimeout(() => setExportSlow(true), EXPORT_SLOW_MS)
    try {
      const jobRef         = (devanning.plan as any)?.source_loading_list?.job_ref || ''
      const plannedDevanAt = (devanning.plan as any)?.planned_devan_at || null
      const rows = buildBlankSheetRows()
      const blob = await withTimeout(
        pdf(
          <BlankDevanSheetPDF
            jobRef={jobRef}
            containerNo={devanning.container_no}
            warehouseCode={warehouseCode}
            plannedDevanAt={plannedDevanAt}
            rows={rows}
            type={type}
          />
        ).toBlob(),
        EXPORT_TIMEOUT_MS,
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const stem = [jobRef, devanning.container_no].filter(Boolean).join('-')
      a.download = `${stem}-Devan-${type === 'full' ? 'Full' : 'Simple'}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      const msg = err instanceof ExportTimeoutError
        ? `PDF generation timed out after ${EXPORT_TIMEOUT_MS / 1000}s — please try again.`
        : 'PDF generation failed: ' + (err?.message || String(err))
      toast(msg, 'error')
    } finally {
      clearTimeout(slowTimer)
      setExportSlow(false)
      exportJobRef.current = null
      setExportJob(null)
    }
  }

  // ── Excel blank sheet download (print + handwrite on the warehouse floor) ────

  const downloadBlankSheetExcel = async (type: 'full' | 'simple' = 'full') => {
    if (!devanning || exportJobRef.current) return
    exportJobRef.current = { kind: 'xlsx', type }
    setExportJob(exportJobRef.current)
    const slowTimer = setTimeout(() => setExportSlow(true), EXPORT_SLOW_MS)
    try {
      const jobRef         = (devanning.plan as any)?.source_loading_list?.job_ref || ''
      const plannedDevanAt = (devanning.plan as any)?.planned_devan_at || null
      const rows = buildBlankSheetRows()
      const blob = await withTimeout(
        generateDevanSheetExcelBlob({
          jobRef,
          containerNo: devanning.container_no,
          warehouseCode,
          plannedDevanAt,
          rows,
          type,
        }),
        EXPORT_TIMEOUT_MS,
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = devanSheetExcelFilename(type, devanning.container_no, jobRef)
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      const msg = err instanceof ExportTimeoutError
        ? `Excel generation timed out after ${EXPORT_TIMEOUT_MS / 1000}s — please try again.`
        : 'Excel generation failed: ' + (err?.message || String(err))
      toast(msg, 'error')
    } finally {
      clearTimeout(slowTimer)
      setExportSlow(false)
      exportJobRef.current = null
      setExportJob(null)
    }
  }

  // Shared button label for the 4 export buttons — spinner + "Generating…" while
  // its own job is running (only one of the 4 can be active at a time), with the
  // ">3s" progress text kicking in once exportSlow flips.
  const exportButtonLabel = (kind: 'pdf' | 'xlsx', type: 'full' | 'simple', idleLabel: string) => {
    if (exportJob?.kind !== kind || exportJob?.type !== type) return idleLabel
    return (
      <span className="inline-flex items-center gap-1.5">
        <ExportSpinner />
        {exportSlow ? `Generating ${kind === 'pdf' ? 'PDF' : 'Excel'}… please wait` : 'Generating…'}
      </span>
    )
  }

  // ── Derived ───────────────────────────────────────────────────────────────────

  const groups       = useMemo(() => groupByGroupName(items), [items])

  // Auto-fit column widths: header text + content samples. Used as default when user hasn't saved a width.
  const wfAutoWidths = useMemo((): Record<string, number> => {
    if (typeof document === 'undefined') return {}
    const cvs = document.createElement('canvas')
    const ctx = cvs.getContext('2d')
    if (!ctx) return {}
    const msr = (t: string, bold = false) => {
      ctx.font = bold ? 'bold 12px sans-serif' : '12px monospace'
      return ctx.measureText(t).width
    }
    const WF_SKIP = new Set(['hold', 'skipdetail', 'pallets', 'check', 'note'])
    const WF_MAX: Record<string, number> = { nm: 300, destination: 200, mode: 160 }
    const result: Record<string, number> = {}
    for (const col of WF_COL_DEFS) {
      if (WF_SKIP.has(col.id)) continue
      let w = msr(col.label, true) + 40   // header + icon-pad + cell-pad
      for (const g of groups) {
        const sample =
          col.id === 'destination' ? (g.items[0]?.delivery_address?.slice(0, 20) ?? '')
          : col.id === 'split'     ? g.groupName
          : col.id === 'splitno'   ? [...new Set(g.items.map(it => it.split_no).filter(Boolean))].slice(0, 3).join(' ')
          : col.id === 'nm'        ? [...new Set(g.items.map(it => it.system_ref).filter(Boolean))].slice(0, 3).join(' ')
          : col.id === 'jobref'    ? [...new Set(g.items.map(it => (it.plan_item as any)?.client_ref).filter(Boolean))].sort().slice(0, 3).join(' ')
          : col.id === 'mode'      ? (g.items[0]?.delivery_method ?? '')
          : col.id === 'qty'       ? String(g.items.reduce((s, it) => s + it.expected_cartons, 0))
          : col.id === 'wt'        ? g.items.reduce((s, it) => s + (it.expected_weight_kg || 0), 0).toFixed(1)
          : col.id === 'cbm'       ? g.items.reduce((s, it) => s + (it.expected_cbm || 0), 0).toFixed(2)
          : ''
        w = Math.max(w, msr(sample) + 20)
      }
      result[col.id] = Math.max(50, Math.min(WF_MAX[col.id] ?? 250, Math.ceil(w)))
    }
    return result
  }, [groups])

  // Priority: user-saved → auto-fit → hardcoded fallback
  const getColWidth = (colId: string): number =>
    savedColWidths[colId] ?? wfAutoWidths[colId] ?? COL_WIDTHS[colId] ?? 80

  const totalExpected = items.reduce((s, it) => s + it.expected_cartons, 0)
  const totalActual   = useMemo(
    () => groups.reduce((s, g) => {
      const isSkip = skipDetail[g.groupName] ?? true
      return s + (isSkip
        ? (manualCartonSum[g.groupName] ?? 0)
        : (pallets[g.groupName] || Array(PALLET_COUNT).fill(0)).reduce((a: number, v: number) => a + v, 0))
    }, 0),
    [groups, pallets, skipDetail, manualCartonSum]
  )
  const sortedGroups = useMemo(() => {
    if (!wfSortCol) return groups
    return [...groups].sort((a, b) => {
      const key = (g: DestGroup) => {
        const pv = pallets[g.groupName] || Array(PALLET_COUNT).fill(0)
        switch (wfSortCol) {
          case 'destination': return g.items[0]?.delivery_address?.slice(0, 16) || ''
          case 'split': return g.groupName
          case 'splitno': return [...new Set(g.items.map(it => it.split_no).filter(Boolean))].sort().join(', ')
          case 'nm': return [...new Set(g.items.map(it => it.system_ref).filter(Boolean))].join(', ')
          case 'jobref': return [...new Set(g.items.map(it => (it.plan_item as any)?.client_ref).filter(Boolean))].sort().join(', ')
          case 'mode': return g.items[0]?.delivery_method || ''
          case 'qty': return g.items.reduce((s, it) => s + it.expected_cartons, 0)
          case 'wt': return g.items.reduce((s, it) => s + (it.expected_weight_kg || 0), 0)
          case 'cbm': return g.items.reduce((s, it) => s + (it.expected_cbm || 0), 0)
          case 'hold': return (holds[g.groupName] ?? false) ? 1 : 0
          case 'tplts': {
            const sk = skipDetail[g.groupName] ?? true
            return sk ? (manualPalletTotal[g.groupName] ?? 0) : pv.filter((v: number) => v > 0).length
          }
          case 'actsum': {
            const sk = skipDetail[g.groupName] ?? true
            return sk ? (manualCartonSum[g.groupName] ?? 0) : pv.reduce((s: number, v: number) => s + v, 0)
          }
          case 'pltlocation': return rowPltLocation[g.groupName] ?? ''
          default: return ''
        }
      }
      const ka = key(a), kb = key(b)
      const cmp = typeof ka === 'number' ? (ka as number) - (kb as number) : String(ka).localeCompare(String(kb))
      return wfSortDir === 'asc' ? cmp : -cmp
    })
  }, [groups, wfSortCol, wfSortDir, pallets, holds, rowPltLocation])

  // B3/Z50.3: same mirrored-fixed-scrollbar pattern as Z34's fix for the Jobs list / B3's
  // LoadingLists wiring — this grid is wide enough (P1-P15 pallet columns) to overflow too.
  const { scrollProps: wfScrollProps, contentRef: wfTableRef, bar: wfScrollBar } = useStickyHScroll([sortedGroups, wfVisibleCols])

  // ── Render ────────────────────────────────────────────────────────────────────

  if (!devanning) {
    return <WarehouseLayout warehouseId={id!}><div className="py-20 text-center text-gray-400">Loading…</div></WarehouseLayout>
  }

  return (
    <WarehouseLayout warehouseId={id!}>

      {/* ── Page header ── */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-2">
          <Link to={`/warehouse/${id}/devannings`} className="text-sm text-blue-600 hover:underline">← Devannings</Link>
          {devanning.plan_id && (
            <Link to={`/warehouse/${id}/plans/${devanning.plan_id}`} className="text-sm text-indigo-600 hover:underline">View Source Plan →</Link>
          )}
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-mono">{devanning.container_no}</h2>
            {(devanning.plan as any)?.source_loading_list?.job_ref && (
              <div className="text-sm text-gray-500 font-medium mt-0.5">Job Ref: <strong className="text-gray-700 dark:text-gray-200">{(devanning.plan as any).source_loading_list.job_ref}</strong></div>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-1">
              {devanning.plan?.vessel    && <span>Vessel: <strong>{devanning.plan.vessel}</strong></span>}
              {devanning.plan?.eta_date  && <span>ETA: <strong>{fmtDate(devanning.plan.eta_date)}</strong></span>}
              {devanning.devanning_started_at   && <span>Started: <strong>{fmtDateTime(devanning.devanning_started_at)}</strong></span>}
              {devanning.devanning_completed_at && <span>Completed: <strong>{fmtDateTime(devanning.devanning_completed_at)}</strong></span>}
              {(devanning.devanning_started_at || devanning.devanning_completed_at) &&
                <span>Devanned by: <strong>{devanning.devanned_by || '—'}</strong></span>}
            </div>
            {/* Haulier + Arrival inline edit */}
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Haulier</label>
                <input
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white w-40"
                  placeholder="e.g. DPD, UPS, DHL"
                  value={haulierInput}
                  onChange={e => setHaulierInput(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Arrival Time</label>
                <input
                  type="datetime-local"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  value={arrivalAtInput}
                  onChange={e => setArrivalAtInput(e.target.value)}
                />
              </div>
              <button onClick={saveHeaderFields} disabled={savingHeader}
                className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingHeader ? '…' : 'Save'}
              </button>
            </div>
          </div>

          {/* Status + totals + actions */}
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-3">
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${DEV_STATUS_COLORS[devanning.status] || 'bg-gray-100 text-gray-600'}`}>
                {devanning.status.charAt(0).toUpperCase() + devanning.status.slice(1)}
              </span>
              {saveStatus === 'saving' && <span className="text-xs text-gray-400">Saving…</span>}
              {saveStatus === 'saved'  && <span className="text-xs text-green-500">Auto-saved ✓ {saveTime}</span>}
              {saveStatus === 'error'  && <span className="text-xs text-red-500">Save error</span>}
            </div>
            <div className="flex gap-3 text-sm font-mono text-gray-600 dark:text-gray-300">
              <span>Exp: <strong>{totalExpected}</strong></span>
              <span>Act: <strong className={totalActual > 0 ? (totalActual === totalExpected ? 'text-green-600' : 'text-orange-500') : 'text-gray-400'}>{totalActual}</strong></span>
            </div>

            {devanning.status === 'awaiting' && (
              <div className="flex flex-col items-end gap-1 mt-1">
                <p className="text-xs text-gray-400 max-w-xs text-right">
                  For paper devan work — print the Excel, add notes by hand, scan/photo the marked-up copy back into the job later.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => downloadBlankSheetPDF('full')} disabled={!!exportJob}
                    className="px-3 py-1.5 text-sm font-semibold bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
                    {exportButtonLabel('pdf', 'full', 'Devan Sheet (Full)')}
                  </button>
                  <button onClick={() => downloadBlankSheetExcel('full')} disabled={!!exportJob}
                    className="px-3 py-1.5 text-sm font-semibold bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50">
                    {exportButtonLabel('xlsx', 'full', 'Devan Sheet (Full) .xlsx')}
                  </button>
                  <button onClick={() => downloadBlankSheetPDF('simple')} disabled={!!exportJob}
                    className="px-3 py-1.5 text-sm font-semibold bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">
                    {exportButtonLabel('pdf', 'simple', 'Devan Sheet (Simple)')}
                  </button>
                  <button onClick={() => downloadBlankSheetExcel('simple')} disabled={!!exportJob}
                    className="px-3 py-1.5 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                    {exportButtonLabel('xlsx', 'simple', 'Devan Sheet (Simple) .xlsx')}
                  </button>
                  <button onClick={handleStartDevanning}
                    className="px-4 py-1.5 text-sm font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow">
                    Start Devanning →
                  </button>
                  <button onClick={handleCancel}
                    className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {devanning.status === 'started' && (
              <div className="flex flex-col items-end gap-1 mt-1">
                <p className="text-xs text-gray-400 max-w-xs text-right">
                  For paper devan work — print the Excel, add notes by hand, scan/photo the marked-up copy back into the job later.
                </p>
                <div className="flex flex-wrap gap-2">
                <button onClick={() => downloadBlankSheetPDF('full')} disabled={!!exportJob}
                  className="px-3 py-1.5 text-sm font-semibold bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
                  {exportButtonLabel('pdf', 'full', 'Devan Sheet (Full)')}
                </button>
                <button onClick={() => downloadBlankSheetExcel('full')} disabled={!!exportJob}
                  className="px-3 py-1.5 text-sm font-semibold bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50">
                  {exportButtonLabel('xlsx', 'full', 'Devan Sheet (Full) .xlsx')}
                </button>
                <button onClick={() => downloadBlankSheetPDF('simple')} disabled={!!exportJob}
                  className="px-3 py-1.5 text-sm font-semibold bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">
                  {exportButtonLabel('pdf', 'simple', 'Devan Sheet (Simple)')}
                </button>
                <button onClick={() => downloadBlankSheetExcel('simple')} disabled={!!exportJob}
                  className="px-3 py-1.5 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {exportButtonLabel('xlsx', 'simple', 'Devan Sheet (Simple) .xlsx')}
                </button>
                <button onClick={handleComplete} disabled={completing}
                  className="px-4 py-1.5 text-sm font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 shadow">
                  {completing ? 'Completing…' : 'Mark Completed ✓'}
                </button>
                <button onClick={handleCancel}
                  className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                  Cancel
                </button>
                </div>
              </div>
            )}

            {devanning.status === 'completed' && (
              <div className="flex flex-wrap gap-2 mt-1">
                <Link to={`/warehouse/${id}/devannings/${did}/report`}
                  className="px-4 py-1.5 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Generate Report →
                </Link>
                <Link to={`/warehouse/${id}/devannings/${did}/notes`}
                  className="px-4 py-1.5 text-sm font-semibold bg-gray-700 text-white rounded-lg hover:bg-gray-800">
                  View Notes →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── WYSIWYG Pallet Grid ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {wfLoaded && (
          <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <ColumnToggleButton cols={WF_COL_DEFS} hidden={wfHidden} onToggle={toggleWfCol}
              onReset={resetWfCols}
              onRecalculate={() => {
                // wfAutoWidths already computed; save only resizable cols (skip fixed-width ones)
                const WF_REC_SKIP = new Set(['hold', 'skipdetail', 'pallets', 'check', 'note'])
                const fitted = Object.fromEntries(
                  Object.entries(wfAutoWidths).filter(([id]) => !WF_REC_SKIP.has(id))
                )
                setWfExtra({ ...wfExtra, widths: { ...fitted, ...savedColWidths } })
              }}
            />
          </div>
        )}
        <div className="overflow-x-auto" {...wfScrollProps}>
          <table ref={wfTableRef as React.RefObject<HTMLTableElement>} className="text-xs border-collapse w-full" style={{ tableLayout: 'fixed', minWidth: '1100px' }}>
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <DndContext sensors={wfSensors} collisionDetection={closestCenter} onDragEnd={handleWfColDragEnd}>
                  <SortableContext items={wfVisibleCols.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                    {wfVisibleCols.map(col => {
                      const w = getColWidth(col.id)
                      const sortable = !['pallets', 'hold', 'skipdetail', 'check', 'note'].includes(col.id)
                      const extraCls = col.id === 'destination'
                        ? 'text-left sticky left-0 bg-gray-100 dark:bg-gray-700 z-10'
                        : col.id === 'split' || col.id === 'splitno' || col.id === 'nm' || col.id === 'jobref' || col.id === 'mode' ? 'text-left'
                        : col.id === 'pallets' ? 'text-center text-blue-600 dark:text-blue-400'
                        : 'text-center'
                      return (
                        <SortableColHeader key={col.id} id={col.id}
                          style={{ width: `${w}px`, minWidth: '50px' }}
                          className={`border border-gray-200 dark:border-gray-600 px-2 py-2 font-semibold whitespace-nowrap ${sortable ? 'cursor-pointer select-none hover:bg-gray-200 dark:hover:bg-gray-600' : ''} ${extraCls}`}
                          onClick={sortable ? () => toggleSort(col.id) : undefined}
                          onResizeEnd={newW => saveColWidth(col.id, newW)}
                          onResizeReset={() => resetColWidth(col.id)}>
                          <span>
                            {col.label}{wfSortCol === col.id ? (wfSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                          </span>
                        </SortableColHeader>
                      )
                    })}
                  </SortableContext>
                </DndContext>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map(group => {
                const pVals           = pallets[group.groupName] || Array(PALLET_COUNT).fill(0)
                const expectedQty     = group.items.reduce((s, it) => s + it.expected_cartons, 0)
                const expectedCbm     = group.items.reduce((s, it) => s + (it.expected_cbm || 0), 0)
                const wtKg            = group.items.reduce((s, it) => s + (it.expected_weight_kg || 0), 0)
                const splitNos        = [...new Set(group.items.map(it => it.split_no).filter(Boolean))].sort().join(', ')
                const sysRefs         = [...new Set(group.items.map(it => it.system_ref).filter(Boolean))].join(' ')
                const jobRefs         = [...new Set(group.items.map(it => (it.plan_item as any)?.client_ref).filter(Boolean))].sort().join('\n')
                // #183: show the full address (never sliced) and fall back to the
                // delivery method when a group has no address at all (e.g. DPD).
                const destination     = group.items[0]?.delivery_address || group.items[0]?.delivery_method || ''
                const delMode         = group.items[0]?.delivery_method || ''
                const isSkip          = skipDetail[group.groupName] ?? true
                const mpt             = manualPalletTotal[group.groupName] ?? 0
                const mcs             = manualCartonSum[group.groupName] ?? 0
                const tPlts           = isSkip ? mpt : pVals.filter(v => v > 0).length
                const actSum          = isSkip ? mcs : pVals.reduce((s, v) => s + v, 0)
                const holdVal         = holds[group.groupName] ?? false
                const actualReceived  = actualCtns[group.groupName] ?? 0
                const checkIcon = (() => {
                  if (isSkip) {
                    // skip=true: only Act Ctns vs Qty matters — no P1-P15 sum to compare
                    if (!actualReceived) return null
                    if (actualReceived === expectedQty) return <span className="text-green-500">✓</span>
                    return <span className="text-yellow-500" title={`Actual (${actualReceived}) ≠ Expected (${expectedQty})`}>⚠</span>
                  }
                  if (!actualReceived && !actSum) return null
                  if (actualReceived > 0 && actSum > 0 && actSum !== actualReceived)
                    return <span className="text-red-500 font-bold" title={`Act Sum (${actSum}) ≠ Actual Received (${actualReceived})`}>✗</span>
                  if (actualReceived > 0 && actualReceived !== expectedQty)
                    return <span className="text-yellow-500" title={`Actual (${actualReceived}) ≠ Expected (${expectedQty})`}>⚠</span>
                  if (actualReceived > 0 && actSum > 0 && actSum === actualReceived && actualReceived === expectedQty)
                    return <span className="text-green-500">✓</span>
                  return <span className="text-orange-500" title={`Off by ${actSum - expectedQty}`}>⚠</span>
                })()

                const bdr = 'border border-gray-200 dark:border-gray-600'
                const cellMap: Record<string, React.ReactNode> = {
                  destination: (
                    <td key="destination" className={`${bdr} p-0 sticky left-0 bg-white dark:bg-gray-800 z-10`}>
                      <div className="px-2 py-1.5 font-mono text-gray-700 dark:text-gray-300"
                        style={{ whiteSpace: 'pre-line', wordBreak: 'break-word', maxHeight: '160px', overflowY: 'auto' }}>
                        {destination || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </div>
                    </td>
                  ),
                  split: (
                    <td key="split" className={`${bdr} px-2 py-1.5 font-semibold text-indigo-700 dark:text-indigo-300 whitespace-nowrap overflow-hidden`}
                      style={{ textOverflow: 'ellipsis' }}>
                      {cleanGroupName(group.groupName)}
                    </td>
                  ),
                  // Z50.5: was whitespace-nowrap+ellipsis (truncated multi-split groups to
                  // "01, 02…") — wrap instead, matching N/M's always-show-everything style.
                  splitno: (
                    <td key="splitno" className={`${bdr} p-0`}>
                      <div className="px-1.5 py-1 font-mono text-gray-500 dark:text-gray-400"
                        style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxHeight: '160px', overflowY: 'auto' }}>
                        {splitNos || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </div>
                    </td>
                  ),
                  nm: (
                    <td key="nm" className={`${bdr} p-0`}>
                      <div className="px-1.5 py-1 font-mono text-gray-500 dark:text-gray-400"
                        style={{ wordBreak: 'break-all', overflowWrap: 'break-word', maxHeight: '160px', overflowY: 'auto' }}>
                        {sysRefs}
                      </div>
                    </td>
                  ),
                  jobref: (
                    <td key="jobref" className={`${bdr} p-0`}>
                      <div className="px-1.5 py-1 font-mono text-blue-700 dark:text-blue-400"
                        style={{ whiteSpace: 'pre-line', maxHeight: '160px', overflowY: 'auto' }}>
                        {jobRefs || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </div>
                    </td>
                  ),
                  mode:    <td key="mode"    className={`${bdr} px-2 py-1.5 text-gray-500 dark:text-gray-400 whitespace-nowrap overflow-hidden`} style={{ textOverflow: 'ellipsis' }}>{delMode}</td>,
                  qty:     <td key="qty"     className={`${bdr} px-2 py-1.5 text-center font-semibold font-mono text-gray-800 dark:text-gray-100`}>{expectedQty}</td>,
                  wt:      <td key="wt"      className={`${bdr} px-2 py-1.5 text-center font-mono text-gray-400`}>{wtKg > 0 ? wtKg.toFixed(1) : '—'}</td>,
                  cbm:     <td key="cbm"     className={`${bdr} px-2 py-1.5 text-center font-mono text-gray-400`}>{expectedCbm > 0 ? expectedCbm.toFixed(2) : '—'}</td>,
                  hold:    (
                    <td key="hold" className={`${bdr} px-2 py-1.5 text-center`}>
                      <input type="checkbox" checked={holdVal}
                        onChange={e => updateHold(group.groupName, group.items, e.target.checked)}
                        className="w-4 h-4 cursor-pointer accent-orange-500" />
                    </td>
                  ),
                  skipdetail: (
                    <td key="skipdetail" className={`${bdr} px-2 py-1.5 text-center`}>
                      <input type="checkbox" checked={isSkip}
                        onChange={e => updateSkipDetail(group.groupName, group.items, e.target.checked)}
                        className="w-4 h-4 cursor-pointer accent-blue-500"
                        title="When checked: enter a single total instead of P1-P15 breakdown" />
                    </td>
                  ),
                  pallets: (
                    <td key="pallets" className={`${bdr} p-0`}>
                      {isSkip ? (
                        <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500 text-[10px] text-center leading-relaxed"
                          style={{ height: '150px' }}>
                          Pallet detail skipped<br/>— enter total in Act Sum →
                        </div>
                      ) : (
                        <div className="grid grid-cols-5" style={{ gridTemplateRows: `repeat(3, 50px)` }}>
                          {pVals.map((val, i) => (
                            <div key={i}
                              className={[
                                'relative',
                                (i + 1) % 5 !== 0 ? 'border-r border-gray-100 dark:border-gray-700' : '',
                                i < 10 ? 'border-b border-gray-100 dark:border-gray-700' : '',
                                val > 0 ? 'bg-blue-50/70 dark:bg-blue-900/20' : '',
                                devanning.status === 'awaiting' ? 'bg-gray-50 dark:bg-gray-700/30' : '',
                              ].filter(Boolean).join(' ')}>
                              <span className="absolute top-0.5 left-1 text-[8px] leading-none text-gray-400 font-medium select-none pointer-events-none">
                                P{i + 1}
                              </span>
                              <input
                                type="number" min="0" step="1"
                                value={val || ''}
                                disabled={devanning.status === 'awaiting'}
                                onChange={e => updatePallet(group.groupName, group.items, i, parseInt(e.target.value) || 0)}
                                className={`absolute inset-0 w-full h-full text-center font-mono pt-3 bg-transparent focus:outline-none focus:ring-inset focus:ring-1 focus:ring-blue-400 ${
                                  devanning.status === 'awaiting'
                                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-gray-800 dark:text-white focus:bg-blue-50 dark:focus:bg-blue-900/30'
                                }`}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  ),
                  tplts: isSkip ? (
                    <td key="tplts" className={`${bdr} p-0 relative`} title="Enter total pallet count">
                      <input type="number" min="0" step="1"
                        value={mpt || ''}
                        onChange={e => updateManualTotal(group.groupName, group.items, parseInt(e.target.value) || 0)}
                        className="absolute inset-0 w-full h-full px-1 py-2 text-center font-mono font-semibold bg-blue-50 dark:bg-blue-900/20 ring-1 ring-inset ring-blue-300 dark:ring-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-800 dark:text-blue-200"
                        placeholder="—" />
                    </td>
                  ) : (
                    <td key="tplts" className={`${bdr} px-2 py-1.5 text-center font-semibold font-mono text-gray-700 dark:text-gray-200`}>{tPlts > 0 ? tPlts : <span className="text-gray-300">—</span>}</td>
                  ),
                  actsum: isSkip ? (
                    <td key="actsum" className={`${bdr} p-0 relative`} title="Enter total carton count">
                      <input type="number" min="0" step="1"
                        value={mcs || ''}
                        onChange={e => updateManualCartonSum(group.groupName, group.items, parseInt(e.target.value) || 0)}
                        className="absolute inset-0 w-full h-full px-1 py-2 text-center font-mono font-semibold bg-blue-50 dark:bg-blue-900/20 ring-1 ring-inset ring-blue-300 dark:ring-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-800 dark:text-blue-200"
                        placeholder="—" />
                    </td>
                  ) : (
                    <td key="actsum" className={`${bdr} px-2 py-1.5 text-center font-semibold font-mono ${actSum > 0 ? (actSum === actualReceived && actualReceived > 0 ? 'text-green-700 dark:text-green-400' : 'text-orange-500 dark:text-orange-400') : 'text-gray-300'}`}>{actSum > 0 ? actSum : '—'}</td>
                  ),
                  pltlocation: (
                    <td key="pltlocation" className={`${bdr} p-0`}>
                      <input type="text"
                        value={rowPltLocation[group.groupName] ?? ''}
                        onChange={e => updatePltLocation(group.groupName, group.items, e.target.value)}
                        className="block w-full px-2 py-1.5 text-xs bg-transparent focus:outline-none focus:ring-inset focus:ring-1 focus:ring-blue-400 text-gray-700 dark:text-gray-300 placeholder-gray-300 dark:placeholder-gray-600"
                        placeholder="—"
                      />
                    </td>
                  ),
                  check:  <td key="check"  className={`${bdr} px-2 py-1.5 text-center text-base leading-none`}>{checkIcon}</td>,
                  note: (
                    <td key="note" className={`${bdr} p-0`}>
                      <textarea
                        value={rowNotes[group.groupName] ?? ''}
                        onChange={e => updateNote(group.groupName, group.items, e.target.value)}
                        className="block w-full px-2 py-1.5 text-xs bg-transparent resize-none focus:outline-none focus:ring-inset focus:ring-1 focus:ring-blue-400 text-gray-700 dark:text-gray-300 placeholder-gray-300 dark:placeholder-gray-600"
                        style={{ minHeight: '150px' }}
                        placeholder="—"
                      />
                    </td>
                  ),
                }
                return (
                  <tr key={group.groupName} className="hover:bg-blue-50/30 dark:hover:bg-blue-900/10">
                    {wfVisibleCols.map(col => cellMap[col.id])}
                  </tr>
                )
              })}

              {/* ── Totals row ── */}
              {groups.length > 0 && (() => {
                const totalPlts = groups.reduce((s, g) => {
                  const sk = skipDetail[g.groupName] ?? true
                  return s + (sk
                    ? (manualPalletTotal[g.groupName] ?? 0)
                    : (pallets[g.groupName] || Array(PALLET_COUNT).fill(0)).filter((v: number) => v > 0).length)
                }, 0)
                const bdr = 'border border-gray-200 dark:border-gray-600'
                const totalCellMap: Record<string, React.ReactNode> = {
                  destination: <td key="destination" className={`${bdr} px-2 py-2 text-right text-gray-500 dark:text-gray-400 uppercase tracking-wide sticky left-0 bg-gray-50 dark:bg-gray-700/50`}>TOTAL</td>,
                  split:      <td key="split"      className={bdr} />,
                  splitno:    <td key="splitno"    className={bdr} />,
                  nm:         <td key="nm"         className={bdr} />,
                  jobref:     <td key="jobref"     className={bdr} />,
                  mode:       <td key="mode"       className={bdr} />,
                  qty:        <td key="qty"        className={`${bdr} px-2 py-2 text-center font-mono text-gray-800 dark:text-gray-100`}>{totalExpected}</td>,
                  wt:         <td key="wt"         className={bdr} />,
                  cbm:        <td key="cbm"        className={bdr} />,
                  hold:       <td key="hold"       className={bdr} />,
                  skipdetail: <td key="skipdetail" className={bdr} />,
                  pallets:    <td key="pallets"    className={bdr} />,
                  tplts:      <td key="tplts"      className={`${bdr} px-2 py-2 text-center font-mono text-gray-700 dark:text-gray-200`}>{totalPlts || '—'}</td>,
                  actsum:     <td key="actsum"     className={`${bdr} px-2 py-2 text-center font-mono ${totalActual > 0 ? (totalActual === totalExpected ? 'text-green-700 dark:text-green-400' : 'text-orange-500') : 'text-gray-300'}`}>{totalActual > 0 ? totalActual : '—'}</td>,
                  pltlocation: <td key="pltlocation" className={bdr} />,
                  check:      <td key="check"      className={`${bdr} px-2 py-2 text-center text-base leading-none`}>{totalActual > 0 ? (totalActual === totalExpected ? <span className="text-green-500">✓</span> : <span className="text-orange-500">⚠</span>) : null}</td>,
                  note:       <td key="note"       className={bdr} />,
                }
                return (
                  <tr className="bg-gray-50 dark:bg-gray-700/50 font-semibold text-xs">
                    {wfVisibleCols.map(col => totalCellMap[col.id])}
                  </tr>
                )
              })()}
            </tbody>
          </table>
        </div>
        <StickyHScrollbar {...wfScrollBar} />

        {groups.length === 0 && (
          <div className="py-12 text-center space-y-3">
            <p className="text-gray-400">No items in this devanning yet.</p>
            <p className="text-xs text-gray-400">The devanning may have been created before plan items were synced.</p>
            <button onClick={handleSyncFromPlan} disabled={syncingFromPlan}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {syncingFromPlan ? 'Syncing…' : 'Sync Items from Plan'}
            </button>
            {syncPlanError && <p className="text-sm text-red-500">{syncPlanError}</p>}
          </div>
        )}
      </div>

      {/* ── Post-completion edit log ── */}
      {devanning.status === 'completed' && editLog.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-200 dark:border-amber-700 overflow-hidden">
          <button
            onClick={() => setShowEditLog(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20">
            <span>Post-Completion Edits ({editLog.length})</span>
            <span>{showEditLog ? '▲' : '▼'}</span>
          </button>
          {showEditLog && (
            <div className="border-t border-amber-100 dark:border-amber-800 divide-y divide-amber-50 dark:divide-amber-900/30">
              {editLog.map(entry => (
                <div key={entry.id} className="px-4 py-2.5 text-xs">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                      {EDIT_FIELD_LABELS[entry.field_changed] ?? entry.field_changed}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{fmtDateTime(entry.changed_at)}</span>
                  </div>
                  {(entry.from_value !== null || entry.to_value !== null) && (
                    <div className="mt-0.5 font-mono text-gray-500 dark:text-gray-400">
                      {entry.from_value} → {entry.to_value}
                    </div>
                  )}
                  <div className="mt-0.5 text-gray-500 dark:text-gray-400">
                    Reason: <em>{entry.reason}</em> · by {entry.changed_by}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Skip-detail confirm dialog ── */}
      {confirmSkipGroup !== null && (() => {
        const grp = groups.find(g => g.groupName === confirmSkipGroup)
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Clear P1–P15 data?</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Switching <strong className="text-gray-700 dark:text-gray-200">{confirmSkipGroup}</strong> to
                "total only" mode will erase the existing P1–P15 pallet breakdown. This cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setConfirmSkipGroup(null)}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Cancel
                </button>
                <button onClick={() => {
                  if (grp) applySkipDetail(confirmSkipGroup, grp.items, true, true)
                  setConfirmSkipGroup(null)
                }}
                  className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Clear & Switch
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── Mismatch dialog ── */}
      {completeDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Quantity Mismatch Detected</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">The following splits have discrepancies between expected and actual quantities:</p>
            <div className="mb-4 space-y-1.5">
              {mismatchList.map(m => (
                <div key={m.groupName} className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded px-3 py-1.5">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate max-w-[200px]">{m.groupName}</span>
                  <span className="text-sm font-mono text-amber-700 dark:text-amber-400 whitespace-nowrap ml-3">
                    Exp {m.expected} · Act {m.actual}{' '}
                    <span className={m.actual - m.expected < 0 ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}>
                      ({m.actual - m.expected > 0 ? '+' : ''}{m.actual - m.expected})
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason for override <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal text-xs ml-1">(min 5 chars)</span>
              </label>
              <textarea rows={3} value={completeReason} onChange={e => setCompleteReason(e.target.value)}
                placeholder="e.g. Short shipped by supplier, confirmed by warehouse manager..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              {completeReason.length > 0 && completeReason.trim().length < 5 && (
                <p className="mt-1 text-xs text-red-500">Reason must be at least 5 characters.</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCompleteDialog(false)}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Continue Editing
              </button>
              <button
                disabled={completing || completeReason.trim().length < 5}
                onClick={() => doComplete(completeReason)}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
                {completing ? 'Completing…' : 'Confirm Completion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3-way cancel choice */}
      {cancelChoice && !cancelSubDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">What do you want to do?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 font-mono">{devanning?.container_no}</p>
            <div className="space-y-2">
              <button onClick={() => setCancelSubDialog('reschedule')}
                className="w-full text-left px-4 py-3 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                <div className="font-semibold text-blue-800 dark:text-blue-200 text-sm">📅 Reschedule</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Devanning delayed, still happens</div>
              </button>
              <button onClick={() => setCancelSubDialog('revert')}
                className="w-full text-left px-4 py-3 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
                <div className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
                  ↩️ {devanning?.status === 'started' ? 'Revert to Awaiting' : 'Revert to Pending'}
                </div>
                <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  {devanning?.status === 'started' ? 'Pause, reopen via Awaiting' : 'Soft-cancel, reshuffle via Plan'}
                </div>
              </button>
              <button onClick={() => setCancelSubDialog('cancel_job')}
                className="w-full text-left px-4 py-3 rounded-xl border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                <div className="font-semibold text-red-800 dark:text-red-200 text-sm">❌ Cancel Job</div>
                <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">Hard-cancel: devanning + plan permanently cancelled</div>
              </button>
            </div>
            <button onClick={() => { setCancelChoice(false); setCancelSubDialog(null) }}
              className="mt-4 w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
              Back
            </button>
          </div>
        </div>
      )}

      {/* Cancel sub-dialog (reason + confirm) */}
      {cancelChoice && cancelSubDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-0.5">
              {cancelSubDialog === 'reschedule' ? 'Reschedule Devanning' : cancelSubDialog === 'revert' ? (devanning?.status === 'started' ? 'Revert to Awaiting' : 'Revert to Pending') : 'Cancel Job'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 font-mono">{devanning?.container_no}</p>
            {cancelSubDialog === 'reschedule' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Date & Time <span className="text-red-500">*</span></label>
                <input type="datetime-local" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            )}
            {cancelSubDialog === 'revert' && (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 mb-4">
                {devanning?.status === 'started' ? 'This will return the devanning to Awaiting status.' : 'This will delete this devanning and return the plan to Pending status.'}
              </p>
            )}
            {cancelSubDialog === 'cancel_job' && (
              <p className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2 mb-4">
                ⚠️ This will permanently cancel both the devanning and its plan. This cannot be undone.
              </p>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason <span className="text-red-500">*</span> <span className="text-gray-400 font-normal text-xs">(min 5 chars)</span></label>
              <textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder="Enter reason..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              {cancelReason.length > 0 && cancelReason.trim().length < 5 && (
                <p className="mt-1 text-xs text-red-500">Reason must be at least 5 characters.</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCancelSubDialog(null)}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                ← Back
              </button>
              <button
                disabled={cancelSaving || cancelReason.trim().length < 5 || (cancelSubDialog === 'reschedule' && !rescheduleDate)}
                onClick={handleCancelAction}
                className={`px-4 py-2 text-sm rounded-lg text-white font-medium disabled:opacity-50 ${cancelSubDialog === 'reschedule' ? 'bg-blue-600 hover:bg-blue-700' : cancelSubDialog === 'revert' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {cancelSaving ? 'Saving…' : cancelSubDialog === 'reschedule' ? 'Confirm Reschedule' : cancelSubDialog === 'revert' ? (devanning?.status === 'started' ? 'Revert to Awaiting' : 'Revert to Pending') : 'Cancel Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inbound confirmation dialog */}
      {inboundDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-2xl">✅</span>
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Devanning Completed</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Move the following {pendingInventoryRows.length} group{pendingInventoryRows.length !== 1 ? 's' : ''} into Inbound stock?
            </p>
            <div className="mb-5 space-y-1.5 max-h-52 overflow-y-auto">
              {pendingInventoryRows.map((row, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded px-3 py-2">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate max-w-[200px]">
                    {(row.group_name as string) || '—'}
                  </span>
                  <span className="text-sm font-mono text-gray-600 dark:text-gray-300 whitespace-nowrap ml-3">
                    {row.cartons as number} ctns{row.pallets_qty ? ` · ${row.pallets_qty as number} plts` : ''}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <a
                href={`/warehouse/${id}/inventory`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline dark:text-blue-400">
                Check current Inbound ↗
              </a>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate(`/warehouse/${id}/devannings/${did}/report`)}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                  Not Now
                </button>
                <button
                  onClick={doMoveToInbound}
                  disabled={inboundPushing}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                  {inboundPushing ? 'Moving…' : 'Move to Inbound'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Post-completion edit reason dialog ── */}
      {showReasonDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Edit Completed Devanning</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              This devanning is marked completed. Please provide a reason for this change.
            </p>
            {pendingEditInfo && (
              <div className="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-xs font-mono text-amber-800 dark:text-amber-300">
                {pendingEditInfo.field}: {pendingEditInfo.from} → {pendingEditInfo.to}
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal text-xs ml-1">(min 5 chars)</span>
              </label>
              <textarea
                rows={3}
                value={reasonInput}
                onChange={e => setReasonInput(e.target.value)}
                autoFocus
                placeholder={'e.g. "T.PLTS was 0 due to UI bug when completed, correcting now"'}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              {reasonInput.length > 0 && reasonInput.trim().length < 5 && (
                <p className="mt-1 text-xs text-red-500">Reason must be at least 5 characters.</p>
              )}
              {sessionReason === null && (
                <p className="mt-1 text-xs text-gray-400">This reason will be reused for any further edits this session.</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={dismissReasonDialog}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button
                onClick={confirmReason}
                disabled={reasonInput.trim().length < 5}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                Save Change
              </button>
            </div>
          </div>
        </div>
      )}

    </WarehouseLayout>
  )
}
